import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import {
	IDropshippingSettings,
	IMPORT_LIST,
	IOrdersList,
	IOrdersParams,
	ImportListParams,
	ORDERS,
	PRODUCTS,
	DROPSHIPPING_SETTINGS,
	MATCH_PRODUCTS,
	BULK_CHECKOUT_SUMMARY,
  ORDER_SHIPPING_ADDRESS
} from "./types";
import {
	createSampleOrder,
	getCategories,
	getImportList,
	getOrders,
	getProducts,
	getDropshippingSettings,
	updateDropshippingSettings,
	updateImportListBulk,
	getAliexpressProducts,
	getAliexpressMatchProducts,
	getBulkCheckoutSummary,
	cancelSubOrder,
	getAliexpressProductsById,
  importAliexpressProduct,
  placeAliexpressOrder,
  getOrderShippingAddress
} from "./requests";
import { IAliexpressMatchProductParams, IAliexpressProductParams, ICategories, IProductList, IProductParams } from "~/types/dropshipping";

export const useGetProducts = (params?: IProductParams) =>
  useInfiniteQuery<IProductList>(
    [...PRODUCTS, params],
    ({ pageParam = 1 }) => getProducts({ ...params, page: pageParam }),
    {
      getNextPageParam: (lastPage) => {
        const nextPage = lastPage.page + 1;
        if (nextPage <= lastPage.total_pages) return nextPage;
      },
    }
  );

export const useGetAliexpressProducts = (params?: IAliexpressProductParams) =>
  useInfiniteQuery<IProductList>(
    [...PRODUCTS, params],
    ({ pageParam = 1 }) =>
      getAliexpressProducts({ ...params, page: pageParam }),
    {
      getNextPageParam: (lastPage) => {
        const nextPage = lastPage.page + 1;
        if (nextPage <= lastPage.total_pages) return nextPage;
      },
    }
  );

export const useGetAliexpressMatchProducts = (params?: IAliexpressMatchProductParams) =>
	useInfiniteQuery<IProductList>(
    [...MATCH_PRODUCTS, params],
    ({ pageParam = 1 }) => {
      if (!params?.product_id && !params?.image)
        return Promise.resolve({ page: 1, total_pages: 1, products: [] });
      return getAliexpressMatchProducts({ ...params, page: pageParam })
    },
    {
			getNextPageParam: (lastPage) => {
				const nextPage = lastPage.page + 1;
				if (nextPage <= lastPage.total_pages) return nextPage;
			},
    }
  );

export const useGetAliexpressProductsById = (product_id: string) =>
  useQuery([], () => getAliexpressProductsById(product_id));

export const useGetCategories = () =>
  useQuery<Array<ICategories>>([], () => getCategories());

export const useGetOrders = (params?: IOrdersParams) =>
  useQuery<IOrdersList>([...ORDERS, params], () => getOrders(params));

export const useGetImportList = (params?: ImportListParams) =>
  useQuery<IProductList>([...IMPORT_LIST, params], () => getImportList(params));

export const useCreateSampleOrder = () => useMutation(createSampleOrder);

export const useDropshippingSettings = () =>
  useQuery<IDropshippingSettings>(DROPSHIPPING_SETTINGS, () =>
    getDropshippingSettings()
  );

export const useUpdateDropshippingSettings = () =>
  useMutation(updateDropshippingSettings);

export const useCancelSubOrder = () => useMutation(cancelSubOrder);

export const useGetBulkCheckoutSummary = (
  subOrderIds: number[] = [],
  orderType: string
) =>
  useQuery([...BULK_CHECKOUT_SUMMARY, orderType, ...subOrderIds], () =>
    getBulkCheckoutSummary(subOrderIds, orderType)
  );

export const useUpdateImportListBulk = () => useMutation(updateImportListBulk);

export const useImportAliexpressProduct = () => useMutation(importAliexpressProduct);

export const usePlaceAliexpressOrder = () => useMutation(placeAliexpressOrder);

export const useOrderShippingAddress = (order_id: number) =>
  useQuery([ORDER_SHIPPING_ADDRESS, order_id], () => getOrderShippingAddress(order_id));